import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Activity, BarChart3, FileClock, LayoutDashboard, LogOut, Menu, MessageSquareText, Settings2, Users, X } from 'lucide-react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { AdminTimeControls } from './AdminTimeControls';

const navItems = [
  { to: '/admin', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/admin/health', label: 'Voice health', icon: Activity },
  { to: '/admin/conversations', label: 'Conversations', icon: MessageSquareText },
  { to: '/admin/users', label: 'Users', icon: Users },
  { to: '/admin/scenarios', label: 'Scenarios', icon: Settings2 },
  { to: '/admin/audit', label: 'Audit log', icon: FileClock },
];

const titles = {
  '/admin': ['Operations overview', 'Activity, engagement, conversions and conversation intelligence.'],
  '/admin/health': ['Voice health', 'Cross-device reliability from microphone to playback.'],
  '/admin/conversations': ['Conversations', 'Review sessions and explicitly access full transcripts.'],
  '/admin/users': ['Users', 'Engagement and completion history by account.'],
  '/admin/scenarios': ['Scenario management', 'Content controls and practice inventory.'],
  '/admin/audit': ['Access audit', 'A trace of sensitive administrator actions.'],
};

export const AdminLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { logout } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [title, subtitle] = titles[location.pathname] || titles['/admin'];
  const showTime = ['/admin', '/admin/health', '/admin/conversations'].includes(location.pathname);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] text-[#1A1C18]" data-testid="admin-shell">
      <button onClick={() => setMobileOpen(true)} className="fixed left-4 top-4 z-40 bg-[#2C5545] p-2 text-white lg:hidden" aria-label="Open admin navigation" data-testid="admin-mobile-menu-button"><Menu className="h-5 w-5" /></button>
      {mobileOpen && <button className="fixed inset-0 z-40 bg-black/35 lg:hidden" onClick={() => setMobileOpen(false)} aria-label="Close admin navigation backdrop" data-testid="admin-mobile-backdrop" />}
      <aside className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-[#1E3F33] text-[#FAF8F5] transition-transform duration-200 lg:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex h-24 items-center justify-between border-b border-white/10 px-6">
          <div><p className="font-serif text-xl">Prosperitas</p><p className="mt-1 text-[10px] uppercase text-white/55">Operations console</p></div>
          <button onClick={() => setMobileOpen(false)} className="p-2 text-white/70 lg:hidden" aria-label="Close admin navigation" data-testid="admin-mobile-close-button"><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-6">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} onClick={() => setMobileOpen(false)} className={({ isActive }) => `flex items-center gap-3 px-4 py-3 text-sm transition-colors rounded-sm ${isActive ? 'bg-white/12 text-white' : 'text-white/65 hover:bg-white/7 hover:text-white'}`} data-testid={`admin-nav-${label.toLowerCase().replace(' ', '-')}`}>
              <Icon className="h-4 w-4" strokeWidth={1.5} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <button onClick={handleLogout} className="flex w-full items-center gap-3 px-4 py-3 text-sm text-white/65 transition-colors hover:bg-white/7 hover:text-white rounded-sm" data-testid="admin-logout-button"><LogOut className="h-4 w-4" strokeWidth={1.5} /> Sign out</button>
        </div>
      </aside>

      <main className="min-h-screen lg:pl-64">
        <header className="sticky top-0 z-30 border-b border-[#DDD7CD] bg-[#FAF8F5]/95 px-5 py-5 backdrop-blur-xl sm:px-8 lg:px-10">
          <div className="flex flex-col gap-5 pl-10 lg:pl-0 xl:flex-row xl:items-end xl:justify-between">
            <div><p className="text-[10px] font-semibold uppercase text-[#D26046]">Administrator</p><h1 className="mt-1 font-serif text-3xl text-[#1A1C18]" data-testid="admin-page-title">{title}</h1><p className="mt-1 text-sm text-[#6B6E69]">{subtitle}</p></div>
            {showTime && <AdminTimeControls />}
          </div>
        </header>
        <div className="px-5 py-8 sm:px-8 lg:px-10"><Outlet /></div>
      </main>
    </div>
  );
};
